/**
 * Valida variáveis de ambiente críticas no boot.
 * Em DEV: imprime avisos no console.
 * Em PROD nativo: loga erro estruturado (não quebra a UI, apenas alerta).
 */
import { Capacitor } from "@capacitor/core";

interface EnvCheck {
  name: string;
  value: string | undefined;
  requiredIn: "always" | "native" | "web";
  hint: string;
}

export function validateRequiredEnv(): void {
  const isNative = Capacitor.isNativePlatform?.() ?? false;
  const platform = Capacitor.getPlatform?.() ?? "web";

  const checks: EnvCheck[] = [
    {
      name: "VITE_SUPABASE_URL",
      value: import.meta.env.VITE_SUPABASE_URL,
      requiredIn: "always",
      hint: "Auto-gerada pelo Lovable Cloud.",
    },
    {
      name: "VITE_SUPABASE_PUBLISHABLE_KEY",
      value: import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY,
      requiredIn: "always",
      hint: "Auto-gerada pelo Lovable Cloud.",
    },
    {
      name: "VITE_PAYMENTS_CLIENT_TOKEN",
      value: import.meta.env.VITE_PAYMENTS_CLIENT_TOKEN,
      requiredIn: "web",
      hint: "Stripe publishable key (pk_test_... ou pk_live_...).",
    },
    {
      name: "VITE_REVENUECAT_APPLE_API_KEY",
      value: import.meta.env.VITE_REVENUECAT_APPLE_API_KEY,
      requiredIn: "native",
      hint: "Public API key do RevenueCat (prefix appl_). Necessária para IAP em iOS.",
    },
    {
      name: "VITE_REVENUECAT_GOOGLE_API_KEY",
      value: import.meta.env.VITE_REVENUECAT_GOOGLE_API_KEY,
      requiredIn: "native",
      hint: "Public API key do RevenueCat (prefix goog_). Necessária para IAP em Android.",
    },
  ];

  const missing: string[] = [];
  for (const c of checks) {
    if (c.value && c.value.length > 0) continue;
    const need =
      c.requiredIn === "always" ||
      (c.requiredIn === "native" && isNative) ||
      (c.requiredIn === "web" && !isNative);
    if (!need) continue;
    // iOS native só precisa Apple; Android só precisa Google.
    if (c.name.includes("APPLE") && platform !== "ios") continue;
    if (c.name.includes("GOOGLE") && platform !== "android") continue;
    missing.push(`${c.name} — ${c.hint}`);
  }

  if (missing.length === 0) return;

  const header = `[env-validation] ${missing.length} variável(is) obrigatória(s) ausente(s) (${platform}):`;
  // eslint-disable-next-line no-console
  console.error(header + "\n  - " + missing.join("\n  - "));

  if (import.meta.env.DEV) {
    // Em dev, joga no toast para visibilidade imediata.
    import("sonner")
      .then(({ toast }) => {
        toast.error("Variáveis de ambiente ausentes", {
          description: missing[0],
          duration: 8000,
        });
      })
      .catch(() => {});
  }
}
